import { Button, Flex, Heading, Image } from "@chakra-ui/react";
import { Get, Post, Put } from "api";
import MultiInputEntries from "components/multiInputEntries";
import { NAVBAR_HEIGHT } from "components/navbar/NavbarAdmin";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { CheckCircleIcon, UnlockIcon } from "@chakra-ui/icons";
import useSuperAdmin from "hooks/useSuperAdmin";
import UploadSuppressionListView from "views/admin/appDetail/components/UploadSuppressionListView";
import RepeatableSetupForm from "./components/RepeatableSetupForm";
import SortableSetupForm from "./components/SortableSetupForm";
import { motion } from "framer-motion";

type AppDetailRouteParams = {
  appId: string;
  partnerId: string;
};

interface RoasGoal {
  dx: string;
  roasGoal: string;
}

interface AppEvent {
  name: string;
  title: string;
  percentage: string;
}

interface AppDetailType {
  appName: string;
  appIcon: string;
  appPlatform: string;
  appId: string;
  roasGoals: RoasGoal[];
}

type GameChallengesRouteParams = {
  appId: string;
  partnerId: string;
};

type FormAppEvent = {
  eventName: string;
  eventTitle: string;
};

type SortableSetup = {
  appEvents: FormAppEvent[];
};

type FormRoasGoal = {
  dx: number;
  roasGoal: number;
};

export type SortableConfig = {
  partnerId: number;
  platformId: number;
  appId: string;
  sortableSetup: SortableSetup;
  roasGoals: FormRoasGoal[];
  roasIapShare: number;
  revenueEventName: string;
  isRevenueValueShared: boolean;
  isSubmission: boolean;
};

type RepeatableSetup = {
  repeatableEventName: string;
  repeatableUnit: string;
  repeatableFinishedByAM: number;
};

export type RepeatableConfig = {
  partnerId: number;
  platformId: number;
  appId: string;
  repeatableSetup: RepeatableSetup;
  roasGoals: FormRoasGoal[];
  roasIapShare: number;
  revenueEventName: string;
  isRevenueValueShared: boolean;
  isSubmission: boolean;
};

type EventType = "repeatable" | "sortable";

const EVENT_TYPE_BUTTONS = [
  {
    label: "Recurring Event",
    value: "repeatable",
  },
  {
    label: "List of Events",
    value: "sortable",
  },
];

const AppDetail = () => {
  const history = useHistory();
  const isSuperAdmin = useSuperAdmin();
  const params = useParams<AppDetailRouteParams>();
  const [isApproved, setApproved] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [processing, setProcessing] = useState<boolean>(false);
  const [appData, setAppData] = useState<AppDetailType>(null);
  const [events, setEvents] = useState<AppEvent[]>([]);
  const [setupLoading, setSetupLoading] = useState<boolean>(true);
  const [repeatableSetup, setRepeatableSetup] = useState<RepeatableConfig[]>(
    []
  );

  const [sortableSetup, setSortableSetup] = useState<SortableConfig[]>([]);
  const [eventType, setEventType] = useState<EventType>("repeatable");
  const [originalEvents, setOriginalEvents] = useState<AppEvent[]>([]);

  useEffect(() => {
    document.title = "Gamelight - App Details";
  }, []);

  const getIDs = (): Array<number | string | null> | null => {
    const parsedPartnerId = parseInt(params.partnerId, 10);
    const appId = params.appId;

    if (isNaN(parsedPartnerId) || !parsedPartnerId || !appId) {
      return null;
    }

    return [parsedPartnerId, appId];
  };

  const [partnerId, appId] = getIDs();

  const getAppDetails = useCallback(
    async ({
      _partnerId,
      _appId,
    }: {
      _partnerId: number | string;
      _appId: number | string;
    }) => {
      if (!_partnerId || !_appId) return;

      setLoading(true);
      try {
        const appDetailResponse = await Post("/manage/partner/app", {
          partnerId: _partnerId,
          appId: _appId,
        });
        if (appDetailResponse) {
          setAppData(appDetailResponse);
        }

        if (
          appDetailResponse.appEvents &&
          appDetailResponse.appEvents.length > 0
        ) {
          const mappedEvents = appDetailResponse.appEvents.map((itm: any) => ({
            title: itm.eventTitle,
            name: itm.eventName,
            percentage: "",
          }));
          setEvents(mappedEvents);
          setOriginalEvents(mappedEvents);
        }

        setApproved(appDetailResponse.isApproved ?? false);
      } catch (error) {
        console.warn(error);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const addNewEventItem = useCallback(() => {
    setEvents((_events) => [
      ..._events,
      { name: "", title: "", percentage: "" },
    ]);
  }, []);

  const handleEventsFieldChange = useCallback(
    (value: string, index: number, type: string) => {
      setEvents((_events) => [
        ..._events.map((event, idx) =>
          index === idx
            ? {
                name: type === "name" ? value : event.name,
                title: type === "title" ? value : event.title,
                percentage: type === "percentage" ? value : event.percentage,
              }
            : event
        ),
      ]);
    },
    []
  );

  const handleEventsRemove = useCallback((index: number) => {
    return setEvents((prevItems) =>
      [...prevItems].filter((_, i) => i !== index)
    );
  }, []);

  const handleEventsSave = async () => {
    setProcessing(true);
    try {
      const eventData = {
        appId: appId,
        appEvents: [
          ...events.map((item: AppEvent) => ({
            eventName: item.name,
            eventTitle: item.title,
          })),
        ],
        partnerId,
      };

      const response = await Put("/manage/partner/app/events", eventData);
      if (!response?.err) {
        setOriginalEvents(events);
      }
    } catch (error) {
      console.warn(error);
    } finally {
      setProcessing(false);
    }
  };

  const handleApprove = async (
    _partnerId: number | string,
    _appId: number | string
  ) => {
    setProcessing(true);
    try {
      await Post("/manage/partner/app/approve", {
        partnerId: _partnerId,
        appId: _appId,
      });

      setApproved(true);
    } catch (error) {
      console.warn(error);
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    if (!appId || !partnerId) return;

    getAppDetails({ _partnerId: partnerId, _appId: appId });
  }, [getAppDetails, appId, partnerId]);

  const fetchSetups = useCallback(async () => {
    setSetupLoading(true);

    try {
      const repeatableSetup = Get(
        `/manage/partner/${params.partnerId}/app/${params.appId}/setup/repeatable`
      );
      const sortableSetup = Get(
        `/manage/partner/${params.partnerId}/app/${params.appId}/setup/sortable`
      );

      const responses = await Promise.all([repeatableSetup, sortableSetup]);
      const tempRepeatableSetup = (responses[0] as any[]).reverse();
      const tempSortableSetup = (responses[1] as any[]).reverse();

      if (tempRepeatableSetup.filter((itm) => itm).length > 0) {
        setEventType("repeatable");
      } else if (tempSortableSetup.filter((itm) => itm).length > 0) {
        setEventType("sortable");
      }

      setRepeatableSetup(tempRepeatableSetup);
      setSortableSetup(tempSortableSetup);
    } catch (error) {
      console.error("setup loading error: ", error);
    } finally {
      setSetupLoading(false);
    }
  }, [params.appId, params.partnerId]);

  useEffect(() => {
    fetchSetups();
  }, [fetchSetups]);
  return (
    <Flex
      flex={1}
      pt={NAVBAR_HEIGHT}
      minH={"90vh"}
      alignItems="flex-start"
      justifyContent={"space-between"}
    >
      <Flex flexDir={"column"}>
        <Flex flexDir={"row"}>
          <Image src={appData?.appIcon} boxSize={8} borderRadius={6} />
          <Heading ml={4} mb={8} fontSize={"2xl"}>
            {appData?.appName} ({appData?.appPlatform})
          </Heading>
        </Flex>
        <MultiInputEntries
          width={800}
          sectionTitle="In-App-Events"
          handleFieldChange={handleEventsFieldChange}
          onRemove={handleEventsRemove}
          handleSave={handleEventsSave}
          addNewItem={addNewEventItem}
          items={events}
          originalItems={originalEvents}
          loading={loading}
          processing={processing}
          inputFields={[
            {
              title: "Event Name",
              inputType: "text",
              keyName: "name",
              placeHolder: "Name",
              charLimit: 60,
            },
            {
              title: "Event Title",
              inputType: "text",
              keyName: "title",
              placeHolder: "Title",
              charLimit: 26,
            },
          ]}
          defaultEditableValue={false}
        />

        <Flex
          mb={4}
          backgroundColor={"white"}
          p={4}
          borderRadius={12}
          position="relative"
        >
          <UploadSuppressionListView appName={appData?.appName ?? ""} />
        </Flex>
        <Flex
          flexDirection={"column"}
          background="#fff"
          borderRadius={12}
          width={"100%"}
          gap={8}
          paddingTop={8}
          paddingX={4}
        >
          <Heading fontSize={"xl"}>AM Setup V2</Heading>
          <Flex
            alignItems={"center"}
            gap={8}
            justifyContent="center"
            width={"100%"}
          >
            {EVENT_TYPE_BUTTONS.map((button) => (
              <Button
                colorScheme={"brand"}
                variant="ghost"
                opacity={eventType === button.value ? 1 : 0.8}
                bgColor={
                  eventType === button.value
                    ? "rgb(74, 52, 255)"
                    : "rgb(92,73,253)"
                }
                _hover={{
                  bgColor: "rgb(74, 52, 255)",
                }}
                size={"lg"}
                flex={1}
                color={"white"}
                onClick={() => {
                  setEventType(button.value as EventType);
                  history.push({
                    search: `?type=${button.value}`,
                  });
                }}
                py={8}
                key={button.value}
                as={motion.button}
                whileHover={{ scale: 1.05, opacity: 1 }}
                initial={{ opacity: 0 }}
                animate={{ opacity: eventType === button.value ? 1 : 0.8 }}
                isDisabled={loading || setupLoading}
              >
                {button.label}
              </Button>
            ))}
          </Flex>
          {loading || setupLoading ? (
            <></>
          ) : eventType === "repeatable" ? (
            <RepeatableSetupForm
              platformId={appData?.appPlatform === "Android" ? 1 : 2}
              initialData={repeatableSetup}
            />
          ) : (
            <SortableSetupForm
              platformId={appData?.appPlatform === "Android" ? 1 : 2}
              initialData={sortableSetup}
            />
          )}
        </Flex>
      </Flex>
      <Flex marginRight={12} marginTop={12} flexDirection={"column"}>
        <Image src={appData?.appIcon} boxSize={56} borderRadius={24} />
        <Button
          mt={6}
          backgroundColor={"rgb(74, 52, 255)"}
          _hover={{
            backgroundColor: isApproved ? "rgb(74, 52, 255)" : "rgb(92,73,253)",
          }}
          color={"white"}
          disabled={isApproved}
          onClick={() => handleApprove(partnerId, appId)}
          //disabled={props.totalPageCount <= props.currentPageNumber}
        >
          {isApproved ? "Active & Running" : "Approve"}
          {isApproved ? (
            <CheckCircleIcon ml={2} w={4} h={4} />
          ) : (
            <UnlockIcon ml={2} w={4} h={4} />
          )}
        </Button>
        {isSuperAdmin && (
          <Button
            colorScheme={"whatsapp"}
            mt={2}
            onClick={() =>
              history.push(`/partner/${partnerId}/app/${appId}/challenges`)
            }
          >
            Initial Setup
          </Button>
        )}
        <Button
          colorScheme={"whatsapp"}
          mt={2}
          onClick={() =>
            history.push(`/partner/${partnerId}/app/${appId}/setup`)
          }
        >
          AM Setup V2
        </Button>
      </Flex>
    </Flex>
  );
};

export default AppDetail;
